import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Devices,
  People,
  Settings,
  Analytics,
  Logout,
  Notifications,
  AccountCircle,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Security,
  Assessment,
  ExpandMore,
  ExpandLess,
  PersonAdd,
  Key,
  Article,
  Category as CategoryIcon,
  Queue as QueueIcon,
  Image as ImageIcon,
  ShoppingCart,
  LocalOffer,
  QuestionAnswer,
  Apps as AppsIcon,
  AutoAwesome as CreativeLookIcon,
  Monitor as MonitorIcon,
  Flag as FlagIcon,
  Face as FaceIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const drawerWidth = 240;

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [usersMenuOpen, setUsersMenuOpen] = useState(false);
  const [analyticsMenuOpen, setAnalyticsMenuOpen] = useState(false);
  const [monitorMenuOpen, setMonitorMenuOpen] = useState(false);

  const menuItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/admin/dashboard' },
    { text: 'Devices', icon: <Devices />, path: '/admin/devices' },
    { 
      text: 'Analytics', 
      icon: <Analytics />, 
      path: '/admin/analytics',
      subItems: [
        { text: 'Swap Analytics', icon: <Assessment />, path: '/admin/analytics/swaps' },
        { text: 'Creative Look Analytics', icon: <CreativeLookIcon />, path: '/admin/analytics/creative-looks' },
        { text: 'Creative Look Results', icon: <CreativeLookIcon />, path: '/admin/analytics/creative-look-results' },
        { text: 'Output Images', icon: <ImageIcon />, path: '/admin/analytics/output-images' },
        { text: 'Purchase Analytics', icon: <ShoppingCart />, path: '/admin/analytics/purchases' }
      ]
    },
    {
      text: 'Users',
      icon: <People />,
      path: '/admin/users',
      subItems: [
        { text: 'Admin Users', icon: <People />, path: '/admin/users' },
        { text: 'App Users', icon: <PersonAdd />, path: '/admin/users/app-users' },
        { text: 'Permissions', icon: <Key />, path: '/admin/users/permissions' },
        { text: 'Age Detection Test', icon: <FaceIcon />, path: '/admin/users/age-test' },
      ]
    },
    { text: 'Categories', icon: <CategoryIcon />, path: '/admin/categories' },
    { text: 'Creative Looks', icon: <CreativeLookIcon />, path: '/admin/creative-looks' },
    { text: 'Featured Apps', icon: <AppsIcon />, path: '/admin/featured-apps' },
    { text: 'Content Management', icon: <Article />, path: '/admin/content' },
    { text: 'FAQ Management', icon: <QuestionAnswer />, path: '/admin/faqs' },
    { text: 'Queue Management', icon: <QueueIcon />, path: '/admin/queue' },
    { text: 'Notifications', icon: <Notifications />, path: '/admin/notifications' },
    { text: 'Offers', icon: <LocalOffer />, path: '/admin/offers' },
    { text: 'Audit Logs', icon: <Security />, path: '/admin/audit-logs' },
    {
      text: 'Monitor',
      icon: <MonitorIcon />,
      path: '/admin/monitor',
      subItems: [
        { text: 'NSFW Monitoring', icon: <Security />, path: '/admin/monitor/nsfw' },
        { text: 'Flagged Reports', icon: <FlagIcon />, path: '/admin/monitor/flagged-reports' },
        { text: 'NSFW Detection Test', icon: <Security />, path: '/admin/monitor/nsfw-test' },
      ],
    },
    { text: 'Settings', icon: <Settings />, path: '/admin/settings' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleProfileMenuClose();
  };

  const drawer = (
    <Box>
      <Toolbar>
        <AdminPanelSettingsIcon sx={{ mr: 2 }} />
        <Typography variant="h6" noWrap component="div">
          Face Swap Admin
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <React.Fragment key={item.text}>
            <ListItem disablePadding>
              <ListItemButton
                selected={location.pathname === item.path || (item.subItems && item.subItems.some(subItem => location.pathname === subItem.path))}
                onClick={() => {
                  if (item.subItems) {
                    if (item.text === 'Users') {
                      setUsersMenuOpen(!usersMenuOpen);
                    } else if (item.text === 'Analytics') {
                      setAnalyticsMenuOpen(!analyticsMenuOpen);
                    } else if (item.text === 'Monitor') {
                      setMonitorMenuOpen(!monitorMenuOpen);
                    }
                  } else {
                    navigate(item.path);
                    if (isMobile) {
                      setMobileOpen(false);
                    }
                  }
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
                {item.subItems && (
                  item.text === 'Users'
                    ? (usersMenuOpen ? <ExpandLess /> : <ExpandMore />)
                    : item.text === 'Analytics'
                    ? (analyticsMenuOpen ? <ExpandLess /> : <ExpandMore />)
                    : item.text === 'Monitor'
                    ? (monitorMenuOpen ? <ExpandLess /> : <ExpandMore />)
                    : null
                )}
              </ListItemButton>
            </ListItem>
            {item.subItems && (
              (item.text === 'Users' && usersMenuOpen) ||
              (item.text === 'Analytics' && analyticsMenuOpen) ||
              (item.text === 'Monitor' && monitorMenuOpen)
            ) && (
              <List component="div" disablePadding>
                {item.subItems.map((subItem) => (
                  <ListItem key={subItem.text} disablePadding>
                    <ListItemButton
                      selected={location.pathname === subItem.path}
                      onClick={() => {
                        navigate(subItem.path);
                        if (isMobile) {
                          setMobileOpen(false);
                        }
                      }}
                      sx={{ pl: 4 }}
                    >
                      <ListItemIcon>{subItem.icon}</ListItemIcon>
                      <ListItemText primary={subItem.text} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {menuItems.find(item => item.path === location.pathname)?.text || 'Admin Panel'}
          </Typography>
          
          <IconButton color="inherit">
            <Badge badgeContent={4} color="error">
              <Notifications />
            </Badge>
          </IconButton>
          
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="primary-search-account-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar sx={{ width: 32, height: 32 }}>
              {user?.name?.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          
          <Menu
            id="primary-search-account-menu"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
          >
            <MenuItem onClick={() => { navigate('/admin/profile'); handleProfileMenuClose(); }}>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={() => { navigate('/admin/settings'); handleProfileMenuClose(); }}>
              <ListItemIcon>
                <Settings fontSize="small" />
              </ListItemIcon>
              Settings
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default AdminLayout;
